import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { Error } from '@material-ui/icons';
import { FlightSearchTypes, SearchModes, SearchStatus } from '../../../../../constants';
import withSpinner from '../../../../../HOCs/withSpinner';
import AddButton from '../../../../AddButton';
import Button from '../../../../Button';
import OneWayFlightInfo from './OneWayFlightInfo';
import Label from '../../../../Label';
import AgentCommission from '../../../AgentCommission';
import currencySymbol from '../../../../../helpers/currencySymbol';
import RemoveFromCompare from '../../../RemoveFromCompare';
import TripBookButton from '../../../../TripConfiguration/ViewTripConfigurationItems/TripBookButton';
import checkIsNdcFare from '../../../../../helpers/checkNdcFare';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import {
  checkDoEnableSelectFlightsBySegment,
  checkIsLoadedIn,
} from '../../../../../store/slices/user';
import checkIsFlightTripSearch from '../../../../../helpers/selectors/checkIsFlightTripSearch';

const FlightInfo = ({
  itinerary,
  tripId,
  doShowPrice,
  doCheckoutOnSelect,
  onCheckout,
  selected,
  onAddToTripClick,
  onRemoveFromTrip,
  isConfirmation,
  showHideFlightDetailElement,
  isViewingBrochure,
  updateBrochurePriceStatus,
  fareAttributesRow,
}) => {
  const router = useRouter();
  const { isViewingCompare } = useSelector((state) => state.compare);
  const { isLoggedIn } = useSelector(checkIsLoadedIn);
  const { selectedSearchMode } = useSelector((state) => state.tripSearch);
  const isUpdatingBrochurePrice =
    isViewingBrochure && updateBrochurePriceStatus === SearchStatus.SEARCHING;

  const isNdcFare = checkIsNdcFare(itinerary);
  const brochure = useSelector((state) => state.brochure.brochures[tripId]);
  const doAllowBook = brochure && brochure.doAllowBook;
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const doShowBookButton =
    (doAllowBook || isLoggedIn) &&
    isBrochurePage &&
    !itinerary.isUnavailableRate &&
    !isUpdatingBrochurePrice;
  const doRenderBookButton = !!doShowBookButton;
  const { validateFlightStatus } = useSelector((store) => store.flight);
  const validateFlightStatusForItinerary =
    validateFlightStatus &&
    validateFlightStatus[itinerary.flightKey] &&
    validateFlightStatus[itinerary.flightKey].status;
  const validateFlightError =
    validateFlightStatus &&
    validateFlightStatus[itinerary.flightKey] &&
    validateFlightStatus[itinerary.flightKey].error;
  const isTripSearch = selectedSearchMode === SearchModes.FLIGHT;
  const isFlightTripSearch = useSelector(checkIsFlightTripSearch(isTripSearch));
  const { searchObj, breadCrumbsIndex } = useSelector(
    flightSearchInput(tripId, isFlightTripSearch),
  );
  const { flightSegments = [] } = searchObj || {};
  let doEnableSelectFlightsBySegment = useSelector(checkDoEnableSelectFlightsBySegment);
  if (
    isViewingBrochure ||
    isViewingCompare ||
    (!isTripSearch && selected) ||
    isConfirmation ||
    searchObj.flightSearchType === FlightSearchTypes.DIRECT_SELL ||
    searchObj.flightSearchType === FlightSearchTypes.ONE_WAY
  ) {
    doEnableSelectFlightsBySegment = false;
  }

  const saveTripStatus = useSelector(
    (store) => (store.trip.selectedTrips[tripId] || {}).saveTripStatus,
  );
  return (
    <div>
      <div className="grid grid-cols-10 email-display-flex">
        <div className="col-span-7 md:col-span-8 flex flex-col">
          <div className="grid gap-2 text-sm md:text-base">
            {itinerary.flightSegments &&
              itinerary.flightSegments.map((flightSegment, index) =>
                !doEnableSelectFlightsBySegment ||
                itinerary.flightSegments.length === 1 ||
                breadCrumbsIndex === index ? (
                  <OneWayFlightInfo flightSegment={flightSegment} key={`${index.toString()}`} />
                ) : null,
              )}
          </div>
        </div>

        <div className="md:flex flex-row-reverse md:flex-col justify-between text-right col-span-3 md:col-span-2 email-normal-line-height email-ml-20 text-sm md:text-base self-start md:self-stretch">
          {doShowPrice && (
            <div>
              <div>
                {itinerary &&
                  itinerary.cost &&
                  withSpinner(
                    () =>
                      !itinerary.isUnavailableRate ? (
                        <>
                          <div className="text-lg font-bold">
                            <NumberFormat
                              value={itinerary.cost.price}
                              displayType="text"
                              thousandSeparator
                              prefix={currencySymbol(itinerary.cost.currencyCode)}
                              fixedDecimalScale="true"
                              decimalScale="2"
                            />
                          </div>
                          <p className="font-normal text-sm">for all travelers</p>
                        </>
                      ) : (
                        <span className="italic">Flight unavailable</span>
                      ),
                    isUpdatingBrochurePrice,
                    'Retrieving updated price',
                    'inline',
                    'xs',
                  )}
              </div>
              {doRenderBookButton && (
                <div className="w-full flex justify-end items-center">
                  <div className="w-20">
                    <TripBookButton
                      tripId={tripId}
                      saveTripStatus={saveTripStatus}
                      buttonColorCssClass="bg-button-theme text-button-theme"
                      buttonGroupStyles={{
                        minWidth: 'unset',
                        paddingTop: '0.25rem',
                        paddingBottom: '0.25rem',
                      }}
                      onBookClick={() => onCheckout()}
                      disabled={isBrochurePage && isNdcFare}
                      disableBookingTooltip="We're sorry, booking NDC fares from the brochure page is not currently supported."
                    />
                  </div>
                </div>
              )}
              {Boolean(validateFlightError) && (
                <Label
                  level="danger"
                  className="text-sm text-left mb-2"
                  text={validateFlightError}
                  icon={<Error className="text-current" />}
                />
              )}
            </div>
          )}
          {(isConfirmation || !tripId) && (
            <div className="inline-block text-center">{showHideFlightDetailElement}</div>
          )}
          {isViewingCompare && (
            <RemoveFromCompare itineraryId={itinerary.flightKey} itinerary={itinerary} />
          )}
        </div>

        <div className="col-span-10 flex flex-col md:flex-row md:items-center pt-2">
          {tripId && !isViewingBrochure && doShowPrice && (
            <div className="flex flex-row items-center gap-2 order-1 md:order-2 w-full md:w-auto md:ml-auto">
              {itinerary.agentCommission > 0 && (
                <div className="flex-shrink-0 self-end">
                  <AgentCommission
                    mode={itinerary.mode}
                    agentCommission={itinerary.agentCommission}
                    currencyCode={itinerary.cost && itinerary.cost.currencyCode}
                    className="text-xs py-0 px-2"
                  />
                </div>
              )}
              <div className="ml-auto">
                {doCheckoutOnSelect ? (
                  <Button size={Button.buttonSizes.SM} onClick={onCheckout}>
                    Select
                  </Button>
                ) : (
                  <AddButton
                    isSelected={selected}
                    onAdd={onAddToTripClick}
                    onRemove={onRemoveFromTrip}
                    addLabel={
                      doEnableSelectFlightsBySegment && breadCrumbsIndex < flightSegments.length - 1
                        ? 'Select'
                        : 'Add to Trip'
                    }
                    removeLabel="Remove"
                    disabled={
                      validateFlightStatusForItinerary === SearchStatus.SEARCHING ||
                      (validateFlightStatusForItinerary === SearchStatus.DONE &&
                        validateFlightError)
                    }
                    doShowLoadingState={validateFlightStatusForItinerary === SearchStatus.SEARCHING}
                  />
                )}
              </div>
            </div>
          )}
          {fareAttributesRow && (
            <div className="order-2 md:order-1 mt-2 md:mt-0 flex-1 min-w-0 overflow-hidden self-end">
              {fareAttributesRow}
            </div>
          )}
          {!doShowPrice && isNdcFare && (
            <div className="text-sm">
              <Label text={<b>NDC Fare</b>} level="" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

FlightInfo.propTypes = {
  itinerary: PropTypes.objectOf(PropTypes.objectOf),
  tripId: PropTypes.string,
  doShowPrice: PropTypes.bool,
  doCheckoutOnSelect: PropTypes.bool,
  onCheckout: PropTypes.func,
  selected: PropTypes.bool,
  onAddToTripClick: PropTypes.func,
  onRemoveFromTrip: PropTypes.func,
  isConfirmation: PropTypes.bool,
  showHideFlightDetailElement: PropTypes.element,
  isViewingBrochure: PropTypes.bool,
  updateBrochurePriceStatus: PropTypes.number,
  fareAttributesRow: PropTypes.element,
};

FlightInfo.defaultProps = {
  itinerary: {},
  tripId: '',
  doShowPrice: true,
  doCheckoutOnSelect: false,
  onCheckout: undefined,
  selected: false,
  onAddToTripClick: undefined,
  onRemoveFromTrip: undefined,
  isConfirmation: false,
  showHideFlightDetailElement: undefined,
  isViewingBrochure: false,
  updateBrochurePriceStatus: undefined,
  fareAttributesRow: undefined,
};
export default FlightInfo;
